import {
  useId,
  useRef,
  useState,
  type ReactNode,
  type KeyboardEvent,
} from 'react';

import { Botao } from './Botao.js';
import { ICONES } from './icones.js';

import './linha.css';

export type AcaoDaLinha = {
  rotulo: string;
  aoAcionar: () => void;
};

export type PropsLinha = {
  titulo: string;
  /** Classe de apresentação específica do título no contexto consumidor. */
  classeDoTitulo?: string;
  /** Segunda linha: o endereço, o bairro, o que explica o título. */
  detalhe?: string;
  /** Canto direito: data, contagem, o dado que se lê de relance. */
  meta?: ReactNode;
  /** Presente, a linha inteira vira um botão. Ausente, é conteúdo estático. */
  aoTocar?: () => void;
  /** As mesmas ações que um dia virão pelo _swipe_. O menu é o caminho
   *  principal, não o atalho: gesto lateral não existe para teclado, para
   *  VoiceOver nem para quem tem pouca firmeza na mão. */
  acoes?: ReadonlyArray<AcaoDaLinha>;
};

/** Uma linha de lista: título, detalhe, meta e, quando há, um menu de ações.
 *
 *  Sai um `<li>`; quem a usa põe o `<ul>`. Sem `aoTocar` não existe botão
 *  algum na linha — um `<button>` que não faz nada é ruído para quem navega
 *  por teclado e para o leitor de tela. */
export function Linha({
  titulo,
  classeDoTitulo,
  detalhe,
  meta,
  aoTocar,
  acoes,
}: PropsLinha) {
  const [aberto, setAberto] = useState(false);
  const alternador = useRef<HTMLButtonElement>(null);
  const idDoMenu = useId();
  const temAcoes = Boolean(acoes && acoes.length > 0);

  // Esc fecha e devolve o foco a quem abriu — o menu não é armadilha de
  // teclado, e sair dele não pode custar a posição na lista. O manipulador
  // vive nos botões, não na `<ul>`: teclado em elemento não interativo é
  // teclado que o VoiceOver nunca alcança.
  function fecharComEsc(evento: KeyboardEvent) {
    if (evento.key !== 'Escape') return;
    evento.stopPropagation();
    setAberto(false);
    alternador.current?.focus();
  }

  const conteudo = (
    <>
      <span className="ds-linha__texto">
        <span
          className={
            classeDoTitulo
              ? `ds-linha__titulo ${classeDoTitulo}`
              : 'ds-linha__titulo'
          }
        >
          {titulo}
        </span>
        {detalhe ? <span className="ds-linha__detalhe">{detalhe}</span> : null}
      </span>
      {meta ? <span className="ds-linha__meta">{meta}</span> : null}
    </>
  );

  return (
    <li className="ds-linha">
      <div className="ds-linha__corpo">
        {aoTocar ? (
          <button type="button" className="ds-linha__alvo" onClick={aoTocar}>
            {conteudo}
          </button>
        ) : (
          <div className="ds-linha__alvo">{conteudo}</div>
        )}
        {temAcoes ? (
          <button
            type="button"
            ref={alternador}
            className="ds-linha__alternador"
            aria-expanded={aberto}
            aria-controls={idDoMenu}
            // O nome diz de quem são as ações: numa lista de trinta pessoas,
            // trinta botões chamados "Ações" são trinta botões idênticos no
            // rotor do VoiceOver.
            aria-label={`Ações de ${titulo}`}
            onKeyDown={fecharComEsc}
            onClick={() => {
              setAberto((estava) => !estava);
            }}
          >
            {ICONES.mais}
          </button>
        ) : null}
      </div>
      {temAcoes ? (
        <ul id={idDoMenu} className="ds-linha__acoes" hidden={!aberto}>
          {acoes?.map((acao) => (
            <li key={acao.rotulo}>
              {/* O `Botao` do sistema, não um botão à mão: a cópia que estava
                  aqui repetia borda, raio, superfície, peso e quebra de linha
                  da variante secundária — e o bloco `prefers-contrast: more`
                  junto —, que é a divergência esperando para acontecer. */}
              <Botao
                variante="secundario"
                onKeyDown={fecharComEsc}
                onClick={() => {
                  setAberto(false);
                  acao.aoAcionar();
                }}
              >
                {acao.rotulo}
              </Botao>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}
